/**
 * Admin Marketing Page
 * 
 * Phase 4: Re-engagement tools for targeting inactive users.
 * Segments users into Never Engaged, Lapsed, and New & Idle.
 * Provides email composer with templates and outreach history.
 */
import React, { useState, useEffect, useCallback } from "react";
import { 
  Users, Mail, Clock, History, RefreshCw, Loader2, 
  UserX, UserMinus, UserPlus, Send, AlertCircle
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { API_URL } from '@/config/api';

import AudienceTable from "./AudienceTable";
import EmailComposerModal from "./EmailComposerModal";
import OutreachHistory from "./OutreachHistory";

const MarketingPage = () => {
  // Segment data
  const [segments, setSegments] = useState(null);
  const [segmentsLoading, setSegmentsLoading] = useState(true);
  const [segmentsError, setSegmentsError] = useState(null);
  
  // Active segment tab
  const [activeSegment, setActiveSegment] = useState("never_engaged");
  
  // Selected users for email
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  // Email composer modal
  const [composerOpen, setComposerOpen] = useState(false);
  
  // Templates
  const [templates, setTemplates] = useState([]);
  
  // View mode: 'audience' or 'history'
  const [viewMode, setViewMode] = useState("audience");

  const fetchSegments = useCallback(async () => {
    setSegmentsLoading(true);
    setSegmentsError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/marketing/segments`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch segments');
      }
      
      const data = await response.json();
      setSegments(data);
    } catch (err) {
      console.error('Error fetching segments:', err);
      setSegmentsError(err.message);
    } finally {
      setSegmentsLoading(false);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/marketing/templates`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  }, []);

  useEffect(() => {
    fetchSegments();
    fetchTemplates();
  }, [fetchSegments, fetchTemplates]);

  const handleSelectUser = (userId, isSelected) => {
    if (isSelected) {
      setSelectedUsers(prev => [...prev, userId]);
    } else {
      setSelectedUsers(prev => prev.filter(id => id !== userId));
    }
  };

  const handleSelectAll = (userIds, isSelected) => {
    if (isSelected) {
      setSelectedUsers(prev => [...new Set([...prev, ...userIds])]);
    } else {
      setSelectedUsers(prev => prev.filter(id => !userIds.includes(id)));
    }
  };

  const handleClearSelection = () => {
    setSelectedUsers([]);
  };

  const handleOpenComposer = () => {
    if (selectedUsers.length === 0) return;
    setComposerOpen(true);
  };

  const handleEmailSent = () => {
    setComposerOpen(false);
    setSelectedUsers([]);
    fetchSegments(); // Refresh counts
  };

  const getSegmentIcon = (segment) => {
    switch (segment) {
      case 'never_engaged': return UserX;
      case 'lapsed': return UserMinus;
      case 'new_and_idle': return UserPlus;
      default: return Users;
    }
  };

  const getSegmentLabel = (segment) => {
    switch (segment) {
      case 'never_engaged': return 'Never Engaged';
      case 'lapsed': return 'Lapsed';
      case 'new_and_idle': return 'New & Idle';
      default: return segment;
    }
  };

  const getSegmentDescription = (segment) => {
    switch (segment) {
      case 'never_engaged': return 'Registered but never added a book or listened';
      case 'lapsed': return 'Had activity >30 days ago, none recently';
      case 'new_and_idle': return 'Registered <14 days ago, no listening yet';
      default: return '';
    }
  };

  const getSegmentCount = (segment) => {
    if (!segments) return 0;
    switch (segment) {
      case 'never_engaged': return segments.never_engaged || 0;
      case 'lapsed': return segments.lapsed || 0;
      case 'new_and_idle': return segments.new_and_idle || 0;
      default: return 0;
    }
  };

  return (
    <div data-testid="admin-marketing-page">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">Marketing</h1>
            <p className="text-slate-400 mt-1">Re-engage inactive users with targeted email campaigns</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant={viewMode === "audience" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("audience")}
              className={viewMode === "audience" 
                ? "bg-blue-600 hover:bg-blue-700" 
                : "border-slate-700 text-slate-300 hover:bg-slate-800"}
              data-testid="view-audience-btn"
            >
              <Users className="w-4 h-4 mr-2" />
              Audience
            </Button>
            <Button
              variant={viewMode === "history" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("history")}
              className={viewMode === "history" 
                ? "bg-blue-600 hover:bg-blue-700" 
                : "border-slate-700 text-slate-300 hover:bg-slate-800"}
              data-testid="view-history-btn"
            >
              <History className="w-4 h-4 mr-2" />
              History
            </Button>
          </div>
        </div>
      </div>

      {viewMode === "audience" ? (
        <>
          {/* Segment Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {['never_engaged', 'lapsed', 'new_and_idle'].map(segment => {
              const Icon = getSegmentIcon(segment);
              const isActive = activeSegment === segment;
              const count = getSegmentCount(segment);
              
              return (
                <Card 
                  key={segment}
                  className={`cursor-pointer transition-all ${
                    isActive 
                      ? 'bg-blue-900/30 border-blue-500/50 ring-1 ring-blue-500/30' 
                      : 'bg-slate-900/50 border-slate-800 hover:border-slate-700'
                  }`}
                  onClick={() => {
                    setActiveSegment(segment);
                    setSelectedUsers([]);
                  }}
                  data-testid={`segment-card-${segment}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className={`p-2 rounded-lg ${
                        segment === 'never_engaged' ? 'bg-red-900/50' :
                        segment === 'lapsed' ? 'bg-amber-900/50' :
                        'bg-emerald-900/50'
                      }`}>
                        <Icon className={`w-5 h-5 ${
                          segment === 'never_engaged' ? 'text-red-400' :
                          segment === 'lapsed' ? 'text-amber-400' :
                          'text-emerald-400'
                        }`} />
                      </div>
                      {segmentsLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                      ) : (
                        <span className="text-2xl font-bold text-white">{count}</span>
                      )}
                    </div>
                    <h3 className="text-sm font-medium text-white mt-3">
                      {getSegmentLabel(segment)}
                    </h3>
                    <p className="text-xs text-slate-400 mt-1">
                      {getSegmentDescription(segment)}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Total Targetable Users */}
          {segments && (
            <div className="flex items-center justify-between mb-6 px-2">
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <Users className="w-4 h-4" />
                <span>Total targetable users: <strong className="text-white">{segments.total_targetable}</strong></span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchSegments}
                disabled={segmentsLoading}
                className="text-slate-400 hover:text-white"
                data-testid="refresh-segments-btn"
              >
                <RefreshCw className={`w-4 h-4 ${segmentsLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          )}

          {/* Error State */}
          {segmentsError && (
            <Card className="bg-red-900/20 border-red-700/50 mb-6">
              <CardContent className="py-6 text-center">
                <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                <p className="text-red-400">{segmentsError}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchSegments}
                  className="mt-4 border-red-700 text-red-400 hover:bg-red-900/30"
                >
                  Retry
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Selection Action Bar */}
          {selectedUsers.length > 0 && (
            <div className="sticky top-[8rem] z-30 bg-slate-900/95 backdrop-blur border border-blue-500/30 rounded-lg p-4 mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Badge variant="secondary" className="bg-blue-600 text-white">
                  {selectedUsers.length} selected
                </Badge>
                {selectedUsers.length > 50 && (
                  <span className="text-amber-400 text-sm flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    Max 50 per send
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearSelection}
                  className="text-slate-400 hover:text-white"
                  data-testid="clear-selection-btn"
                >
                  Clear
                </Button>
                <Button
                  size="sm"
                  onClick={handleOpenComposer}
                  disabled={selectedUsers.length === 0 || selectedUsers.length > 50}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="compose-email-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Compose Email
                </Button>
              </div>
            </div>
          )}

          {/* Audience Table */}
          <AudienceTable
            segment={activeSegment}
            selectedUsers={selectedUsers}
            onSelectUser={handleSelectUser}
            onSelectAll={handleSelectAll}
          />
        </>
      ) : (
        /* History View */
        <OutreachHistory />
      )}

      {/* Email Composer Modal */}
      <EmailComposerModal
        open={composerOpen}
        onOpenChange={setComposerOpen}
        selectedUserIds={selectedUsers}
        templates={templates}
        onEmailSent={handleEmailSent}
      />
    </div>
  );
};

export default MarketingPage;
